import clsx from "clsx";
import { CircleAlert, FileJson, Upload, Check } from "lucide-react";
import React from "react";
import PropTypes from "prop-types";

export function JsonFileDropzone({ validateFields, onFile }) {
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [fileName, setFileName] = React.useState(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isValid, setIsValid] = React.useState(false);
  const fileInputRef = React.useRef(null);

  function processFile(file) {
    setFileName(file.name);
    setIsValid(false);
    var reader = new FileReader();
    reader.onload = function (event) {
      try {
        setErrorMessage(null);
        var parsed = JSON.parse(event.target.result);

        for (var i = 0; i < validateFields.length; i++) {
          if (!(validateFields[i] in parsed)) {
            throw new Error(
              "Invalid credentials file. Missing json key: " +
                validateFields[i],
            );
          }
        }

        setIsValid(true);
        onFile(parsed);
      } catch (error) {
        console.error(error);
        setErrorMessage(error.message);
        onFile(null);
      }
    };
    reader.readAsText(file);
  }

  function onChange(event) {
    const file = event.target.files[0];
    if (file) processFile(file);
  }

  function onDragOver(e) {
    e.preventDefault();
    setIsDragging(true);
  }

  function onDragLeave() {
    setIsDragging(false);
  }

  function onDrop(e) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={onChange}
        style={{ display: "none" }}
      />
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => fileInputRef.current.click()}
        className={clsx(
          "mt-3 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center transition-colors",
          isDragging
            ? "bg-surface-selected border-blue-400"
            : errorMessage
              ? "border-status-error/30 bg-status-error-tint hover:border-borderError"
              : isValid
                ? "bg-surface-default border-status-success/40 hover:border-status-success/60"
                : "border-borderDefault bg-surface-default hover:bg-surface-hover-subtle hover:border-gray-400",
        )}
      >
        <div
          className={clsx(
            "mb-3 rounded-full p-3",
            errorMessage
              ? "bg-status-error-tint"
              : isValid
                ? "bg-status-success-tint"
                : "bg-surface-selected",
          )}
        >
          {errorMessage ? (
            <CircleAlert size={20} className="text-red-500" />
          ) : isValid ? (
            <Check size={20} className="text-green-500" />
          ) : (
            <Upload size={20} className="text-textActionSelected" />
          )}
        </div>
        {errorMessage ? (
          <>
            <div className="text-textError flex items-center gap-2 font-medium">
              <FileJson size={16} className="shrink-0 text-red-400" />
              <span className="truncate">{fileName}</span>
            </div>
            <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
            <p className="mt-1 text-xs text-red-400">
              Click or drop to try again
            </p>
          </>
        ) : fileName ? (
          <>
            <div className="text-textSecondary flex items-center gap-2 font-medium">
              <FileJson size={16} className="text-iconMinimal shrink-0" />
              <span className="truncate">{fileName}</span>
            </div>
            <p className="text-iconMinimal mt-1 text-xs">
              Click or drop to replace
            </p>
          </>
        ) : (
          <>
            <p className="text-textSecondary font-medium">
              Drag or drop your JSON file here
            </p>
            <p className="text-textSubtle mt-1 text-sm">
              Service account credentials from Google Cloud Console
            </p>
            <p className="text-iconMinimal mt-3 text-xs">.json files only</p>
          </>
        )}
      </div>
    </>
  );
}

JsonFileDropzone.propTypes = {
  validateFields: PropTypes.arrayOf(PropTypes.string).isRequired,
  onFile: PropTypes.func.isRequired,
};
